import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectPersonalProjects, selectPersonalProjectExpenses } from '@/utils/store';
import { CATEGORY_ICONS } from '@/utils/format';
import { useCurrency } from '@/hooks/useCurrency';
import PageHeader from '@/components/shared/PageHeader';
import AddPersonalProjectModal from '@/components/shared/AddPersonalProjectModal';
import AddTransactionModal from '@/components/shared/AddTransactionModal';
import ViewToggle from '@/components/ui/ViewToggle';
import { Plus, ArrowLeft, Pencil, Trash2, ChevronLeft, ChevronRight, CheckCircle2, Circle, Copy } from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-success-100 text-success-700 dark:bg-success-500/10 dark:text-success-400',
  completed: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  paused: 'bg-warning-100 text-warning-700 dark:bg-warning-500/10 dark:text-warning-400',
};

export default function PersonalProjects() {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const personalProjects = useStore(selectPersonalProjects);
  const transactions = useStore((s) => s.transactions);
  const removePersonalProject = useStore((s) => s.removePersonalProject);
  const duplicatePersonalProject = useStore((s) => s.duplicatePersonalProject);
  const updatePersonalProjectItem = useStore((s) => s.updatePersonalProjectItem);

  const [modalOpen, setModalOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  const now = new Date();
  const viewDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthKey = viewDate.toISOString().slice(0, 7);
  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const shiftMonth = (dir) => setMonthOffset((p) => p + dir);

  const selected = personalProjects.find((p) => p.id === selectedId);

  const handleAdd = () => {
    setEditProject(null);
    setModalOpen(true);
  };

  const handleEdit = (proj) => {
    setEditProject(proj);
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    removePersonalProject(id);
    if (selectedId === id) setSelectedId(null);
  };

  const handleToggleItem = (projectId, itemId, currentCompleted) => {
    updatePersonalProjectItem(projectId, itemId, { completed: !currentCompleted });
  };

  // Detail view
  if (selected) {
    const projTx = transactions.filter((tx) => tx.personalProjectId === selected.id && tx.date?.startsWith(monthKey));
    const allProjTx = transactions.filter((tx) => tx.personalProjectId === selected.id);
    const totalSpent = allProjTx.reduce((s, tx) => s + tx.amount, 0);
    const totalEstimated = selected.items.reduce((s, i) => s + (Number(i.estimatedCost) || 0), 0);
    const remaining = (selected.budget || 0) - totalSpent;
    const budgetPct = selected.budget > 0 ? Math.round((totalSpent / selected.budget) * 100) : 0;
    const completedItems = selected.items.filter((i) => i.completed).length;

    return (
      <div className="space-y-4 animate-fade-in">
        <button
          onClick={() => setSelectedId(null)}
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
        >
          <ArrowLeft size={16} />
          {t('personalProjects.title')}
        </button>

        {/* Header */}
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
            style={{ backgroundColor: selected.color + '20' }}
          >
            {selected.icon}
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-display font-bold text-gray-900 dark:text-white">{selected.name}</h1>
            <div className="flex items-center gap-2">
              {selected.description && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{selected.description}</span>
              )}
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${STATUS_COLORS[selected.status]}`}>
                {t(`personalProjects.status${selected.status.charAt(0).toUpperCase() + selected.status.slice(1)}`)}
              </span>
            </div>
            {selected.deadline && (
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {t('personalProjects.deadline')}: {selected.deadline}
              </div>
            )}
          </div>
          <button
            onClick={() => handleEdit(selected)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
          >
            <Pencil size={16} />
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('personalProjects.budget')}</div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(selected.budget || 0)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('personalProjects.estimated')}</div>
            <div className="text-lg font-bold text-brand-600 dark:text-brand-400">{formatCurrency(totalEstimated)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('personalProjects.spent')}</div>
            <div className="text-lg font-bold text-danger-600 dark:text-danger-400">{formatCurrency(totalSpent)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('personalProjects.remaining')}</div>
            <div className={`text-lg font-bold ${remaining >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
              {formatCurrency(remaining)}
            </div>
          </div>
        </div>

        {/* Budget progress bar */}
        {selected.budget > 0 && (
          <div>
            <div className="flex justify-between text-[10px] text-gray-400 mb-1">
              <span>{budgetPct}% {t('personalProjects.spent').toLowerCase()}</span>
              <span>{formatCurrency(totalSpent)} / {formatCurrency(selected.budget)}</span>
            </div>
            <div className="h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${budgetPct > 100 ? 'bg-danger-500' : budgetPct > 80 ? 'bg-warning-500' : 'bg-success-500'}`}
                style={{ width: `${Math.min(100, budgetPct)}%` }}
              />
            </div>
          </div>
        )}

        {/* Add expense button */}
        <button
          onClick={() => setTxModalOpen(true)}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400 text-sm font-medium hover:bg-danger-100 dark:hover:bg-danger-500/20 transition-colors border border-danger-200 dark:border-danger-500/20"
        >
          <Plus size={16} />
          {t('personalProjects.addExpense')}
        </button>

        {/* Items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('personalProjects.items')} ({completedItems}/{selected.items.length})
            </h2>
          </div>
          {selected.items.length === 0 ? (
            <div className="card text-center py-6 text-sm text-gray-400 dark:text-gray-500">
              {t('personalProjects.noItems')}
            </div>
          ) : (
            <div className="space-y-2">
              {selected.items.map((item) => (
                <div
                  key={item.id}
                  className={`card flex items-center gap-3 py-3 px-4 cursor-pointer transition-colors ${item.completed ? 'opacity-70' : ''}`}
                  onClick={() => handleToggleItem(selected.id, item.id, item.completed)}
                >
                  {item.completed ? (
                    <CheckCircle2 size={20} className="text-success-500 shrink-0" />
                  ) : (
                    <Circle size={20} className="text-gray-300 dark:text-gray-600 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${item.completed ? 'line-through text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                      {item.name}
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${item.completed ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                    {formatCurrency(Number(item.estimatedCost) || 0)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Items estimated total */}
          {selected.items.length > 0 && (
            <div className="mt-2 flex justify-between text-xs text-gray-400 px-4">
              <span>{t('personalProjects.estimated')}</span>
              <span className="font-bold">{formatCurrency(totalEstimated)}</span>
            </div>
          )}
        </div>

        {/* Month nav for transactions */}
        <div className="flex items-center justify-between">
          <button onClick={() => shiftMonth(-1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
            <ChevronLeft size={20} />
          </button>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{monthLabel}</span>
          <button onClick={() => shiftMonth(1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Transactions */}
        <div>
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('personalProjects.transactions')}</h2>
          {projTx.length === 0 ? (
            <div className="card text-center py-6 text-sm text-gray-400 dark:text-gray-500">
              {t('personalProjects.noTransactions')}
            </div>
          ) : (
            <div className="space-y-2">
              {projTx.map((tx) => (
                <div key={tx.id} onClick={() => setEditingTx(tx)} className="card flex items-center gap-3 py-3 px-4 cursor-pointer hover:border-brand-300 dark:hover:border-brand-600 transition-colors">
                  <span className="text-lg">{CATEGORY_ICONS[tx.category] || '📌'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{tx.description}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t(`categories.${tx.category}`)} · {tx.date}</div>
                  </div>
                  <span className="text-sm font-bold text-danger-600 dark:text-danger-400">
                    -{formatCurrency(tx.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <AddPersonalProjectModal open={modalOpen} onClose={() => { setModalOpen(false); setEditProject(null); }} project={editProject} />
        <AddTransactionModal open={txModalOpen} onClose={() => setTxModalOpen(false)} defaultType="expense" defaultPersonalProjectId={selected.id} />
        <AddTransactionModal open={!!editingTx} onClose={() => setEditingTx(null)} transaction={editingTx} />
      </div>
    );
  }

  const STATUSES = ['active', 'paused', 'completed'];

  // Project card component (shared between list and kanban)
  const ProjectCard = ({ proj }) => {
    const allProjTx = transactions.filter((tx) => tx.personalProjectId === proj.id);
    const totalSpent = allProjTx.reduce((s, tx) => s + tx.amount, 0);
    const pct = proj.budget > 0 ? Math.round((totalSpent / proj.budget) * 100) : 0;
    const completedItems = proj.items.filter((i) => i.completed).length;

    return (
      <div
        className="card hover:border-brand-300 dark:hover:border-brand-600 transition-colors cursor-pointer group relative"
        onClick={() => setSelectedId(proj.id)}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center text-xl"
            style={{ backgroundColor: proj.color + '20' }}
          >
            {proj.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 dark:text-white text-sm">{proj.name}</span>
              {viewMode === 'list' && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${STATUS_COLORS[proj.status]}`}>
                  {t(`personalProjects.status${proj.status.charAt(0).toUpperCase() + proj.status.slice(1)}`)}
                </span>
              )}
            </div>
            {proj.description && (
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">{proj.description}</span>
            )}
            {proj.budget > 0 && (
              <div className="mt-1.5">
                <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${pct > 100 ? 'bg-danger-500' : pct > 80 ? 'bg-warning-500' : 'bg-success-500'}`}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                  <span>{formatCurrency(totalSpent)} / {formatCurrency(proj.budget)}</span>
                  <span>{completedItems}/{proj.items.length} items · {pct}%</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hover actions */}
        <div className="absolute top-2 right-2 hidden group-hover:flex gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); duplicatePersonalProject(proj.id); }}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-brand-600 transition-colors"
          >
            <Copy size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(proj); }}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-brand-600 transition-colors"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(proj.id); }}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-danger-500 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    );
  };

  // List / Kanban view
  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={t('personalProjects.title')}
        hint={t('common.hints.personalProjects')}
        action={
          <div className="flex items-center gap-2">
            <ViewToggle view={viewMode} onChange={setViewMode} />
            <button
              onClick={handleAdd}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
            >
              <Plus size={16} />
              {t('personalProjects.addNew')}
            </button>
          </div>
        }
      />

      {personalProjects.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-4xl mb-3">🏗️</div>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
            {t('personalProjects.noProjects')}
          </p>
          <button
            onClick={handleAdd}
            className="mt-4 px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            {t('personalProjects.addNew')}
          </button>
        </div>
      ) : viewMode === 'list' ? (
        <div className="space-y-3">
          {personalProjects.map((proj) => (
            <ProjectCard key={proj.id} proj={proj} />
          ))}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
          {STATUSES.map((status) => {
            const items = personalProjects.filter((p) => p.status === status);
            return (
              <div key={status} className="flex-shrink-0 w-72 md:flex-1 md:min-w-0">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${STATUS_COLORS[status]}`}>
                    {t(`personalProjects.status${status.charAt(0).toUpperCase() + status.slice(1)}`)}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">{items.length}</span>
                </div>
                <div className="space-y-2 min-h-[100px] bg-gray-50 dark:bg-gray-900/50 rounded-xl p-2">
                  {items.length === 0 ? (
                    <div className="text-center py-8 text-xs text-gray-400 dark:text-gray-500">
                      {t('common.columnEmpty')}
                    </div>
                  ) : (
                    items.map((proj) => <ProjectCard key={proj.id} proj={proj} />)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddPersonalProjectModal open={modalOpen} onClose={() => { setModalOpen(false); setEditProject(null); }} project={editProject} />
    </div>
  );
}
